import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Sun, Moon, MessageCircle } from 'lucide-react';
import { NAV_LINKS, site, waUrl, DEFAULT_WA_MESSAGE } from '../data/site';
import { useTheme } from '../context/ThemeContext';

function Logo() {
  return (
    <Link to="/" data-testid="nav-link-home" className="flex items-center">
      <img
        src={site.logo}
        alt="Grupo Majo — Engenharia, Processos & Intermediações"
        className="h-14 w-auto rounded-lg bg-white px-2 py-1.5 shadow-md md:h-16"
      />
    </Link>
  );
}

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  const linkCls = ({ isActive }) =>
    `relative whitespace-nowrap font-display text-[11px] font-bold uppercase tracking-[0.08em] transition-colors duration-200 hover:text-gold-light ${
      isActive ? 'text-gold' : 'text-white/80'
    }`;

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 border-b border-white/10 bg-navy-deep backdrop-blur-xl transition-all duration-300 ${
        scrolled ? 'py-2 shadow-[0_10px_40px_-15px_rgba(0,0,0,0.6)]' : 'py-3'
      }`}
      data-testid="navbar"
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-5 lg:px-8">
        <Logo />
        <nav className="hidden items-center gap-3.5 xl:gap-5 lg:flex">
          {NAV_LINKS.slice(1).map((l) => (
            <NavLink key={l.path} to={l.path} className={linkCls} data-testid={`nav-link-${l.id}`}>
              {l.short}
            </NavLink>
          ))}
        </nav>
        <div className="flex items-center gap-2.5">
          <button
            onClick={toggleTheme}
            data-testid="theme-toggle-button"
            aria-label={theme === 'light' ? 'Ativar modo escuro' : 'Ativar modo claro'}
            className="grid h-10 w-10 place-items-center rounded-full border border-white/20 text-white transition-colors hover:border-gold hover:text-gold-light"
          >
            {theme === 'light' ? <Moon size={17} /> : <Sun size={17} />}
          </button>
          <Link
            to="/simulador"
            data-testid="nav-cta-simulador"
            className="btn-gold hidden !px-5 !py-2.5 text-[12px] md:inline-flex"
          >
            Simular Orçamento
          </Link>
          <button
            onClick={() => setOpen(!open)}
            data-testid="mobile-menu-toggle"
            aria-label="Abrir menu"
            className="grid h-10 w-10 place-items-center rounded-full border border-white/20 text-white lg:hidden"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-x-0 top-0 z-[-1] min-h-screen bg-navy-deep px-6 pb-10 pt-28 lg:hidden"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col gap-1">
              {NAV_LINKS.map((l, i) => (
                <motion.div
                  key={l.path}
                  initial={{ opacity: 0, x: -18 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i }}
                >
                  <NavLink
                    to={l.path}
                    className={({ isActive }) =>
                      `block border-b border-white/10 py-4 font-display text-2xl font-bold tracking-tight ${
                        isActive ? 'text-gold' : 'text-white'
                      }`
                    }
                  >
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }} className="mt-6 flex flex-col gap-3">
                <Link to="/simulador" className="btn-gold" data-testid="mobile-cta-simulador">Simular Orçamento</Link>
                <a
                  href={waUrl(DEFAULT_WA_MESSAGE)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-outline text-white"
                  data-testid="mobile-cta-whatsapp"
                >
                  <MessageCircle size={16} /> WhatsApp {site.contact.phoneDisplay}
                </a>
              </motion.div>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
